/**
 * IntakeWindowGate — the window-gate redirect guard for the intake-gated
 * surfaces (member-onboarding-flow spec, task 9.3).
 *
 * This is the §B "direct navigation" guard from the design
 * (`.kiro/specs/member-onboarding-flow/design.md`, "Architecture → B. Intake-
 * window-gated application/profile visibility"). It answers the pure question
 * "is there anywhere to apply right now?" via `useActiveIntakeSeasons()` (the
 * thin adapter over the pure `activeIntakeSeasons(seasons, now)` core) and, when
 * no `Active_Intake_Season` exists, redirects a direct request for the
 * `Application_Surface` or the `Profile_Surface` back to the `Portal_Dashboard`
 * — rendering no form and, by never mounting the protected surface, creating no
 * Participation on the redirect path (Req 3.4, 6.6).
 *
 * Behavior:
 *   - **Flag OFF** — the `Application_Form_Flag` (`isApplicationFormEnabled()` /
 *     `VITE_FEATURE_APPLICATION_FORM`) is the outermost switch. Req 3.4/6.6 are
 *     scoped to "WHERE the Application_Form_Flag is enabled", so when the flag
 *     is off this gate is a pass-through: the wrapped surface renders exactly as
 *     it did before this feature (legacy behavior unchanged).
 *   - **Flag ON, loading** — while the Season read is in flight
 *     (`status === 'loading'`) render the existing `CrownLoader` and DO NOT
 *     prematurely redirect; only a *resolved* empty set triggers the redirect.
 *   - **Flag ON, empty active set** (`status === 'unresolved'`) — render
 *     `<Navigate to="/portal" replace/>`; no form mounts, no Participation is
 *     created (Req 3.4).
 *   - **Flag ON, non-empty active set** (`status === 'resolved'`) — render the
 *     protected surface (`children`).
 *
 * The flag check lives in the outer component and the `useActiveIntakeSeasons`
 * hook lives in the inner component so the hook (and its Season read) never runs
 * on the flag-off path — mirroring the `ApplyHost` → `ApplyRendererHost` split
 * already in this directory (a stable env-derived flag never changes the hook
 * ordering between renders).
 *
 * Validates: Requirements 3.4, 6.6
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';

import CrownLoader from '@/components/ui/CrownLoader';
import { isApplicationFormEnabled } from '@/config/featureFlags';
import { useActiveIntakeSeasons } from '@/hooks/useActiveIntakeSeasons';

export interface IntakeWindowGateProps {
  /** The intake-gated surface rendered only while an Active_Intake_Season exists. */
  children: React.ReactNode;
}

/**
 * The flag-ON gate body: consult the active-intake-season set and either wait
 * (loading), redirect (empty), or render the protected surface (non-empty).
 * Split out so its `useActiveIntakeSeasons` read never runs on the flag-off
 * pass-through path.
 */
function IntakeWindowGateInner({ children }: IntakeWindowGateProps): React.ReactElement {
  const { status } = useActiveIntakeSeasons();

  // Loading: the Season read is still in flight — wait for resolution rather
  // than prematurely redirecting (Req 3.4 keys off a resolved empty set).
  if (status === 'loading') {
    return (
      <div data-testid="intake-window-gate-loading">
        <CrownLoader message="Loading…" />
      </div>
    );
  }

  // Resolved to an empty Active_Intake_Season set: redirect to the dashboard,
  // render no form, and create no Participation (Req 3.4, 6.6).
  if (status === 'unresolved') {
    return <Navigate to="/portal" replace />;
  }

  // At least one Active_Intake_Season exists — render the protected surface.
  return <>{children}</>;
}

export function IntakeWindowGate({ children }: IntakeWindowGateProps): React.ReactElement {
  // Flag OFF → legacy behavior unchanged: this gate is a pass-through (Req 3.4 /
  // 6.6 are scoped to the enabled flag).
  if (!isApplicationFormEnabled()) {
    return <>{children}</>;
  }

  return <IntakeWindowGateInner>{children}</IntakeWindowGateInner>;
}

export default IntakeWindowGate;
